// helpdesk-frontend/src/components/QuickAccessModal/QuickAccessModal.js

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./QuickAccessModal.module.css";
import { useUI } from "../../hooks/useUI";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import { useAuth } from "../../hooks/useAuth";
import { shakeModal } from "../../utils/modalAttention";
import {
    FiGrid,
    FiMessageSquare,
    FiBell,
    FiRss,
    FiImage,
    FiShield,
    FiCalendar,
    FiBook,
    FiHelpCircle,
    FiLayers,
    FiMonitor,
    FiFileText,
    FiUserPlus,
    FiArchive,
    FiPlusSquare,
    FiX,
    FiInbox,
} from "react-icons/fi";

const QuickAccessModal = () => {
    const { isQuickAccessOpen, closeQuickAccess, openNewTicketModal, isTicketCreateVisible } = useUI();

    // [U2] Escape zamyka okno — jak każde okno w aplikacji (Modal / szuflady).
    useEffect(() => {
        if (!isQuickAccessOpen) return undefined;
        const naKlawisz = (e) => {
            if (e.key === "Escape") closeQuickAccess();
        };
        document.addEventListener("keydown", naKlawisz);
        return () => document.removeEventListener("keydown", naKlawisz);
    }, [isQuickAccessOpen, closeQuickAccess]);
    const { hasPermission } = useAuth();
    const navigate = useNavigate();

    if (!isQuickAccessOpen) return null;

    const handleNavigate = (path) => {
        navigate(path);
        closeQuickAccess();
    };

    const sections = [
        {
            title: "Zgłoszenia",
            items: [
                // Widget „Nowe zgłoszenie" (BRIEF-0003) — skrót znika, gdy admin ukryje moduł 'ticketCreate'.
                ...(isTicketCreateVisible ? [{ icon: <FiPlusSquare />, label: "Nowe zgłoszenie", action: () => { openNewTicketModal(); closeQuickAccess(); } }] : []),
                { icon: <FiMessageSquare />, label: "Moje zgłoszenia", path: localizedPath("myTickets") },
                ...(hasPermission("TicketView") ? [{ icon: <FiMessageSquare />, label: "Obsługa zgłoszeń", path: localizedPath("tickets") }] : []),
                { icon: <FiLayers />, label: "Tablica Kanban", path: localizedPath("kanban") },
            ],
        },
        {
            title: "Informacje",
            items: [
                { icon: <FiRss />, label: "Aktualności", path: localizedPath("news") },
                { icon: <FiInbox />, label: "Centrum komunikacji", path: localizedPath("notificationCenter") },
                { icon: <FiImage />, label: "Galeria zdjęć", path: localizedPath("gallery") },
            ],
        },
        {
            title: "Narzędzia",
            items: [
                ...(hasPermission("DashboardAccess") ? [{ icon: <FiGrid />, label: "Dashboard", path: localizedPath("dashboard") }] : []),
                { icon: <FiCalendar />, label: "Kalendarz", path: localizedPath("calendar") },
                { icon: <FiBook />, label: "Książka adresowa", path: localizedPath("addressBook") },
                { icon: <FiHelpCircle />, label: "Baza wiedzy", path: localizedPath("knowledgeBase") },
            ],
        },
        {
            title: "Moduły",
            items: [
                ...(hasPermission("ItamInvoiceView") ? [{ icon: <FiFileText />, label: "ITAM - Faktury", path: localizedPath("itamInvoices") }] : []),
                ...(hasPermission("ItamDeviceView") ? [{ icon: <FiMonitor />, label: "ITAM - Urządzenia", path: localizedPath("itamDevices") }] : []),
                // [I-b-5] JEDNO wejście do onboardingu NA OSOBĘ. Stary moduł wniosków sprzętowych jest
                // archiwum i wchodzi się do niego z nowego okna — dwa skróty o tej samej nazwie znaczyłyby
                // dwa moduły „nowego pracownika" i żadnej odpowiedzi, który jest prawdziwy.
                ...(hasPermission("HrOnboardView") ? [{ icon: <FiUserPlus />, label: "OnBoarding", path: localizedPath("hrOnboarding") }] : []),
                // [BLOKADA 1, 2026-09-02] …ale kto ma WYŁĄCZNIE klucz starego modułu, musi mieć dokąd wejść.
                // Wcześniej ten skrót przepięto na nowy moduł i na klucz HrOnboardView (explicit-only),
                // więc posiadacz samego OnboardingView stracił jedyną drogę do SWOICH wniosków — a nowe
                // wejście do archiwum siedzi wewnątrz strony, która sama stoi za tamtym kluczem.
                // Nazwa mówi wprost, że to archiwum, więc nie da się go pomylić z modułem prowadzącym.
                ...(hasPermission("OnboardingView") && !hasPermission("HrOnboardView")
                    ? [{ icon: <FiArchive />, label: "OnBoarding — archiwum", path: localizedPath("onboardingRequests") }]
                    : []),
                { icon: <FiShield />, label: "Sygnalista", path: localizedPath("sygnalista") },
                { icon: <FiRss />, label: "Formularz kontaktowy", path: localizedPath("contactForm") },
            ],
        },
    ];

    return (
        // [U2, W-10] klik w tło NIE zamyka — potrząsa oknem; zamknięcie tylko przez ✕ lub Escape.
        <div className={styles.overlay} onClick={shakeModal}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Szybki dostęp">
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Szybki dostęp</h2>
                    <button type="button" className={styles.closeButton} onClick={closeQuickAccess} aria-label="Zamknij">
                        <FiX size={20} />
                    </button>
                </div>
                <div className={styles.grid}>
                    {sections.map((section) => (
                        <div key={section.title} className={styles.section}>
                            <h3 className={styles.sectionTitle}>{section.title}</h3>
                            <div className={styles.sectionItems}>
                                {section.items.map((item, idx) => (
                                    <button
                                        key={idx}
                                        className={styles.item}
                                        onClick={() => {
                                            if (item.action) {
                                                item.action();
                                            } else if (item.path) {
                                                handleNavigate(item.path);
                                            }
                                        }}
                                    >
                                        <span className={styles.itemIcon}>{item.icon}</span>
                                        <span className={styles.itemLabel}>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default QuickAccessModal;
